import React, { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { isDarkMode } from '@localstack/services';
import { Stack, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';

export const SectionTitle = styled('span')(({ theme }) => ({
  color: isDarkMode(theme) ? theme.palette.grey[200] : theme.palette.grey[900],
  fontSize: '18px',
  fontWeight: 500,
  lineHeight: '24px',
}));

export const SectionContainer = styled('div')(({ theme }) => ({
  backgroundColor: isDarkMode(theme) ? theme.palette.background.default : theme.palette.common.white,
  borderRadius: '5px',
  border: `1px solid ${theme.palette.divider}`,
}));
interface PageSectionProps {
  title: string;
  children?: React.ReactNode;
}

export const PageSection = ({ title, children }: PageSectionProps): JSX.Element => {
  return (
    <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
      <SectionTitle>{title}</SectionTitle>
      <SectionContainer>{children}</SectionContainer>
    </div>
  );
};

interface GridProps {
  columns?: string;
}

const Grid = styled('div')<GridProps>(({ theme, columns = '1fr auto' }) => ({
  display: 'grid',
  gridTemplateColumns: columns,
  alignItems: 'center',
  padding: theme.spacing(2),
  columnGap: '16px',
  rowGap: '0',
}));

export const GridComponent = ({
  columns,
  children,
  style,
}: {
  columns?: string;
  children: ReactNode;
  style?: React.CSSProperties;
}): JSX.Element => {
  return (
    <Grid columns={columns} style={style}>
      {children}
    </Grid>
  );
};

/** Divider that spans across all columns in a grid */
export const Divider = styled('div')(({ theme }) => ({
  gridColumn: '1 / -1', // Span all columns
  height: '1px',
  backgroundColor: theme.palette.divider,
  margin: '12px 0',
}));

/** Width of the column left of a feature list, so that checkmarks start at the same x in every section */
export const PLAN_INFO_WIDTH = '400px';

export const FeatureList = ({ features }: { features: string[] }): JSX.Element => {
  return (
    <Stack gap="7px">
      {features.map((feature) => (
        <RowWithCheckmark key={feature}>{feature}</RowWithCheckmark>
      ))}
    </Stack>
  );
};

export const RowWithCheckmark = ({ children }: { children: React.ReactNode }): JSX.Element => {
  return (
    <Stack
      direction="row"
      gap="4px"
      sx={{ color: (theme) => (isDarkMode(theme) ? theme.palette.grey[400] : theme.palette.grey[500]) }}
    >
      <Check fontSize="small" color="inherit" />
      <Typography variant="body1">{children}</Typography>
    </Stack>
  );
};
